import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchSkillFamilies,
  fetchFamilyMembers,
  fetchPendingFamilySample,
  fetchFamilyReviewStats,
  createSkillFamily,
  reviewSkillFamily,
  addSkillToFamily,
  removeSkillFromFamily,
  fetchAllSkills,
  triggerTranslation,
  fetchSkillFamilyIdsMissingTranslations,
  type SkillFamily,
  type RefItem,
  type FamilyMember,
  type SkillFamilyReviewStatus,
} from '../../lib/referentialService';

type ListFilter = 'all' | 'pending' | 'confirmed' | 'dismissed' | 'manual';

const REVIEW_BADGE: Record<SkillFamilyReviewStatus, { label: string; className: string }> = {
  pending: { label: 'À valider', className: 'bg-amber-100 text-amber-800' },
  confirmed: { label: 'Validée', className: 'bg-green-100 text-green-800' },
  dismissed: { label: 'Rejetée', className: 'bg-gray-100 text-gray-600' },
};

const SkillFamilies: React.FC = () => {
  const [families, setFamilies] = useState<SkillFamily[]>([]);
  const [allSkills, setAllSkills] = useState<RefItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFamily, setSelectedFamily] = useState<SkillFamily | null>(null);
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  const [listFilter, setListFilter] = useState<ListFilter>('confirmed');
  const [stats, setStats] = useState({ pending: 0, confirmed: 0, dismissed: 0, manual: 0 });

  const [sampleMode, setSampleMode] = useState(false);
  const [sample, setSample] = useState<SkillFamily[]>([]);
  const [sampleIndex, setSampleIndex] = useState(0);
  const [sampleLoading, setSampleLoading] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [sampleSize, setSampleSize] = useState(20);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newFamilyCode, setNewFamilyCode] = useState('');
  const [newFamilyName, setNewFamilyName] = useState('');
  const [newFamilyDesc, setNewFamilyDesc] = useState('');
  const [creating, setCreating] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [translatingLabel, setTranslatingLabel] = useState<string | null>(null);

  const [searchSkill, setSearchSkill] = useState('');
  const [addingSkill, setAddingSkill] = useState<string | null>(null);
  const [memberSearch, setMemberSearch] = useState('');

  const loadStats = useCallback(async () => {
    try {
      setStats(await fetchFamilyReviewStats());
    } catch (err) {
      console.error('Failed to load family stats', err);
    }
  }, []);

  const loadFamilies = useCallback(async () => {
    setLoading(true);
    try {
      const opts =
        listFilter === 'manual'
          ? { source: 'manual' as const }
          : listFilter === 'all'
            ? undefined
            : { source: 'auto_label' as const, reviewStatus: listFilter as SkillFamilyReviewStatus };

      const [fams, skills] = await Promise.all([
        fetchSkillFamilies(opts),
        allSkills.length ? Promise.resolve(allSkills) : fetchAllSkills(),
      ]);
      setFamilies(fams);
      if (!allSkills.length) setAllSkills(skills);
      await loadStats();
    } catch (err) {
      console.error('Failed to load families', err);
    } finally {
      setLoading(false);
    }
  }, [listFilter, allSkills, loadStats]);

  useEffect(() => {
    loadFamilies();
  }, [listFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectFamily = useCallback(async (family: SkillFamily) => {
    setSelectedFamily(family);
    setLoadingMembers(true);
    setSearchSkill('');
    setMemberSearch('');
    try {
      const members = await fetchFamilyMembers(family.id);
      // Tri : secteur → poste → libellé
      members.sort((a, b) =>
        (a.sectorName || '').localeCompare(b.sectorName || '', 'fr')
        || (a.positionName || '').localeCompare(b.positionName || '', 'fr')
        || a.nameFr.localeCompare(b.nameFr, 'fr'),
      );
      setFamilyMembers(members);
    } catch (err) {
      console.error('Failed to load family members', err);
      setFamilyMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  }, []);

  const startSampleReview = async () => {
    setSampleLoading(true);
    setSampleMode(true);
    setSampleIndex(0);
    setSelectedFamily(null);
    try {
      const items = await fetchPendingFamilySample(sampleSize);
      setSample(items);
      if (items[0]) await selectFamily(items[0]);
    } catch (err) {
      alert(`Erreur: ${err}`);
      setSampleMode(false);
    } finally {
      setSampleLoading(false);
    }
  };

  const handleReview = async (action: 'confirmed' | 'dismissed') => {
    const current = sampleMode ? sample[sampleIndex] : selectedFamily;
    if (!current) return;
    setReviewing(true);
    try {
      await reviewSkillFamily(current.id, action);
      setStats((s) => {
        const next = { ...s };
        if (current.reviewStatus === 'pending') next.pending = Math.max(0, next.pending - 1);
        if (current.reviewStatus === 'confirmed') next.confirmed = Math.max(0, next.confirmed - 1);
        if (current.reviewStatus === 'dismissed') next.dismissed = Math.max(0, next.dismissed - 1);
        if (action === 'confirmed') next.confirmed += 1;
        if (action === 'dismissed') next.dismissed += 1;
        return next;
      });
      setFamilies((prev) => prev.filter((f) => f.id !== current.id));

      if (sampleMode) {
        const next = sampleIndex + 1;
        if (next < sample.length) {
          setSampleIndex(next);
          await selectFamily(sample[next]);
        } else {
          setSampleMode(false);
          setSelectedFamily(null);
          setFamilyMembers([]);
          alert(`Échantillon terminé (${sample.length} familles).`);
          loadFamilies();
        }
      } else {
        setSelectedFamily(null);
        setFamilyMembers([]);
      }
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setReviewing(false);
    }
  };

  const handleCreateFamily = async () => {
    if (!newFamilyCode.trim() || !newFamilyName.trim()) return;
    setCreating(true);
    try {
      const created = await createSkillFamily({
        code: newFamilyCode,
        nameFr: newFamilyName,
        description: newFamilyDesc || undefined,
      });
      setFamilies((prev) => [...prev, created]);
      setShowCreateModal(false);
      setNewFamilyCode('');
      setNewFamilyName('');
      setNewFamilyDesc('');
      loadStats();
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setCreating(false);
    }
  };

  const handleTranslateAll = async () => {
    setTranslating(true);
    try {
      const ids = await fetchSkillFamilyIdsMissingTranslations();

      if (ids.length === 0) {
        alert('Rien à faire : toutes les familles ont déjà les 7 langues.');
        return;
      }

      const CHUNK = 50;
      let translated = 0;
      const errors: string[] = [];
      const totalChunks = Math.ceil(ids.length / CHUNK);

      for (let i = 0; i < ids.length; i += CHUNK) {
        const chunkIdx = Math.floor(i / CHUNK) + 1;
        setTranslatingLabel(`Traduction… ${chunkIdx}/${totalChunks} (${translated} ok)`);

        const result = await triggerTranslation('skill_families', ids.slice(i, i + CHUNK));
        translated += result?.translated || 0;
        if (result?.error) errors.push(result.error);
        if (result?.errors?.length) errors.push(...result.errors);
      }

      if (errors.length > 0 && translated === 0) {
        alert(`Échec traduction (${ids.length} restantes).\n${errors.slice(0, 3).join('\n')}`);
      } else {
        alert(
          `Traduction terminée : ${translated}/${ids.length} famille(s) mises à jour.`
          + (errors.length ? `\nAvertissements: ${errors.slice(0, 2).join('; ')}` : ''),
        );
      }
      await loadFamilies();
    } catch (err) {
      alert(`Erreur traduction: ${err}`);
    } finally {
      setTranslating(false);
      setTranslatingLabel(null);
    }
  };

  const handleAddSkill = async (skillId: string) => {
    if (!selectedFamily) return;
    setAddingSkill(skillId);
    try {
      await addSkillToFamily(skillId, selectedFamily.id);
      const skill = allSkills.find((s) => s.id === skillId);
      if (skill) setFamilyMembers((prev) => [...prev, skill]);
      setFamilies((prev) => prev.map((f) =>
        f.id === selectedFamily.id ? { ...f, memberCount: (f.memberCount || 0) + 1 } : f
      ));
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setAddingSkill(null);
    }
  };

  const handleRemoveSkill = async (skillId: string) => {
    if (!selectedFamily) return;
    try {
      await removeSkillFromFamily(skillId, selectedFamily.id);
      setFamilyMembers((prev) => prev.filter((m) => m.id !== skillId));
      setFamilies((prev) => prev.map((f) =>
        f.id === selectedFamily.id ? { ...f, memberCount: Math.max(0, (f.memberCount || 0) - 1) } : f
      ));
    } catch (err) {
      alert(`Erreur: ${err}`);
    }
  };

  const memberIds = new Set(familyMembers.map((m) => m.id));
  const filteredAvailableSkills = allSkills.filter((s) =>
    !memberIds.has(s.id) &&
    (searchSkill.length < 2 || s.nameFr.toLowerCase().includes(searchSkill.toLowerCase()))
  );

  const uniquePositions = new Set(familyMembers.map((m) => m.positionId).filter(Boolean)).size;
  const uniqueSectors = new Set(familyMembers.map((m) => m.sectorId).filter(Boolean)).size;

  const filteredMembers = memberSearch.trim().length < 2
    ? familyMembers
    : familyMembers.filter((m) => {
      const q = memberSearch.toLowerCase();
      return (
        m.nameFr.toLowerCase().includes(q)
        || (m.sectorName || '').toLowerCase().includes(q)
        || (m.positionName || '').toLowerCase().includes(q)
        || (m.subsectorName || '').toLowerCase().includes(q)
        || (m.code || '').toLowerCase().includes(q)
      );
    });

  const filterTabs: { key: ListFilter; label: string; count?: number }[] = [
    { key: 'pending', label: 'À valider', count: stats.pending },
    { key: 'confirmed', label: 'Validées', count: stats.confirmed },
    { key: 'dismissed', label: 'Rejetées', count: stats.dismissed },
    { key: 'manual', label: 'Manuelles', count: stats.manual },
    { key: 'all', label: 'Toutes' },
  ];

  if (loading && families.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#00244F]/20 border-t-[#00244F] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Familles transversales</h1>
          <p className="text-sm text-gray-500 mt-1">
            Libellé identique = validé auto · échantillon optionnel pour contrôler
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!sampleMode && (
            <>
              <div className="flex items-center gap-1.5">
                <label className="text-xs text-gray-500">Échantillon</label>
                <select
                  value={sampleSize}
                  onChange={(e) => setSampleSize(Number(e.target.value))}
                  className="text-sm border border-gray-200 rounded-lg px-2 py-1.5"
                >
                  {[10, 20, 50].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={startSampleReview}
                disabled={stats.pending === 0 || sampleLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors disabled:opacity-40"
              >
                {sampleLoading ? 'Chargement…' : 'Valider un échantillon'}
              </button>
              <button
                onClick={handleTranslateAll}
                disabled={translating || families.length === 0}
                className="px-4 py-2 text-sm font-medium text-[#00244F] bg-white border border-[#00244F]/30 hover:bg-[#00244F]/5 rounded-lg transition-colors disabled:opacity-40"
              >
                {translating ? (translatingLabel || 'Traduction…') : 'Traduire (7 langues)'}
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 text-sm font-medium text-white bg-[#00244F] hover:bg-[#001B3D] rounded-lg transition-colors"
              >
                + Manuelle
              </button>
            </>
          )}
          {sampleMode && (
            <button
              onClick={() => {
                setSampleMode(false);
                setSelectedFamily(null);
                loadFamilies();
              }}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
            >
              Quitter l&apos;échantillon
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'À valider', value: stats.pending, color: 'text-amber-700 bg-amber-50 border-amber-100' },
          { label: 'Validées', value: stats.confirmed, color: 'text-green-700 bg-green-50 border-green-100' },
          { label: 'Rejetées', value: stats.dismissed, color: 'text-gray-600 bg-gray-50 border-gray-100' },
          { label: 'Manuelles', value: stats.manual, color: 'text-[#00244F] bg-blue-50 border-blue-100' },
        ].map((s) => (
          <div key={s.label} className={`rounded-xl border px-4 py-3 ${s.color}`}>
            <div className="text-2xl font-bold">{s.value.toLocaleString('fr-FR')}</div>
            <div className="text-xs mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {sampleMode && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-amber-900">
              Mode échantillon — {Math.min(sampleIndex + 1, sample.length)} / {sample.length}
            </p>
            <p className="text-xs text-amber-700 mt-0.5">
              Validez si le regroupement a du sens (même geste / compétence). Rejetez les faux positifs évidents.
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <button
              onClick={() => handleReview('dismissed')}
              disabled={reviewing || !sample[sampleIndex]}
              className="px-3 py-2 text-sm font-medium text-red-700 bg-white border border-red-200 hover:bg-red-50 rounded-lg disabled:opacity-40"
            >
              Rejeter
            </button>
            <button
              onClick={() => handleReview('confirmed')}
              disabled={reviewing || !sample[sampleIndex]}
              className="px-3 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-40"
            >
              Valider
            </button>
          </div>
        </div>
      )}

      {!sampleMode && (
        <div className="flex flex-wrap gap-2">
          {filterTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                setListFilter(tab.key);
                setSelectedFamily(null);
              }}
              className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                listFilter === tab.key
                  ? 'bg-[#00244F] text-white border-[#00244F]'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className={`ml-1.5 text-xs ${listFilter === tab.key ? 'text-white/70' : 'text-gray-400'}`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Family list */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-700">
              {sampleMode
                ? `Échantillon (${sample.length})`
                : `${families.length} famille${families.length > 1 ? 's' : ''}`}
            </h3>
          </div>
          <div className="divide-y divide-gray-50 max-h-[70vh] overflow-y-auto">
            {(sampleMode ? sample : families).map((fam, idx) => (
              <button
                key={fam.id}
                onClick={() => {
                  if (sampleMode) setSampleIndex(idx);
                  selectFamily(fam);
                }}
                className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                  selectedFamily?.id === fam.id ? 'bg-blue-50 border-l-2 border-[#00244F]' : ''
                } ${sampleMode && idx === sampleIndex ? 'ring-1 ring-inset ring-amber-300' : ''}`}
              >
                <div className="text-sm font-medium text-gray-900 line-clamp-2">{fam.nameFr}</div>
                <div className="flex items-center flex-wrap gap-1.5 mt-1">
                  {fam.source === 'auto_label' && (
                    <span className="px-1.5 py-0.5 text-[10px] bg-indigo-50 text-indigo-700 rounded-full">
                      Auto
                    </span>
                  )}
                  <span className={`px-1.5 py-0.5 text-[10px] rounded-full ${REVIEW_BADGE[fam.reviewStatus].className}`}>
                    {REVIEW_BADGE[fam.reviewStatus].label}
                  </span>
                  <span className="px-1.5 py-0.5 text-[10px] bg-gray-100 text-gray-500 rounded-full">
                    {fam.memberCount || 0} membres
                  </span>
                </div>
              </button>
            ))}
            {(sampleMode ? sample : families).length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-gray-400">
                {listFilter === 'pending'
                  ? 'Aucune famille en attente — lancez le pré-groupage si besoin'
                  : 'Aucune famille'}
              </div>
            )}
          </div>
        </div>

        {/* Family detail + members */}
        <div className="lg:col-span-2">
          {!selectedFamily ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <p className="text-sm text-gray-500">
                Sélectionner une famille pour inspecter ses membres
              </p>
              {stats.pending > 0 && !sampleMode && (
                <p className="text-xs text-gray-400 mt-2">
                  Astuce : utilisez « Valider un échantillon » pour revue rapide (~20 familles).
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100 flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">
                      « {selectedFamily.nameFr} »
                    </h3>
                    <p className="text-xs text-gray-400 font-mono mt-0.5">{selectedFamily.code}</p>
                    {selectedFamily.description && (
                      <p className="text-xs text-gray-500 mt-1">{selectedFamily.description}</p>
                    )}
                    {!loadingMembers && familyMembers.length > 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        {familyMembers.length} compétences · {uniquePositions} postes · {uniqueSectors} secteur{uniqueSectors > 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                  {selectedFamily.source === 'auto_label' && !sampleMode && (
                    <div className="flex gap-2 shrink-0">
                      {selectedFamily.reviewStatus !== 'dismissed' && (
                        <button
                          onClick={() => handleReview('dismissed')}
                          disabled={reviewing}
                          className="px-2.5 py-1.5 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-lg disabled:opacity-40"
                        >
                          Rejeter
                        </button>
                      )}
                      {selectedFamily.reviewStatus !== 'confirmed' && (
                        <button
                          onClick={() => handleReview('confirmed')}
                          disabled={reviewing}
                          className="px-2.5 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-40"
                        >
                          Valider
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {loadingMembers ? (
                  <div className="p-8 text-center">
                    <div className="w-6 h-6 border-2 border-gray-200 border-t-[#00244F] rounded-full animate-spin mx-auto" />
                  </div>
                ) : (
                  <div className="p-4 space-y-3">
                    {familyMembers.length === 0 ? (
                      <p className="text-sm text-gray-400 text-center py-4">Aucun membre</p>
                    ) : (
                      <>
                        {familyMembers.length > 8 && (
                          <input
                            type="text"
                            value={memberSearch}
                            onChange={(e) => setMemberSearch(e.target.value)}
                            placeholder="Filtrer par secteur, poste, compétence…"
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                          />
                        )}
                        <div className="border border-gray-100 rounded-lg overflow-hidden max-h-[28rem] overflow-y-auto">
                          <table className="w-full text-left text-sm">
                            <thead className="bg-gray-50 sticky top-0 z-10">
                              <tr className="text-xs text-gray-500 uppercase tracking-wide">
                                <th className="px-3 py-2 font-medium">Secteur</th>
                                <th className="px-3 py-2 font-medium">Sous-secteur</th>
                                <th className="px-3 py-2 font-medium">Poste</th>
                                <th className="px-3 py-2 font-medium">Compétence</th>
                                {selectedFamily.source === 'manual' && (
                                  <th className="px-3 py-2 font-medium w-10" />
                                )}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                              {filteredMembers.map((m) => (
                                <tr key={m.id} className="hover:bg-slate-50/80">
                                  <td className="px-3 py-2 text-xs text-gray-700 whitespace-nowrap">
                                    {m.sectorName || <span className="text-gray-300">—</span>}
                                  </td>
                                  <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                                    {m.subsectorName || <span className="text-gray-300">—</span>}
                                  </td>
                                  <td className="px-3 py-2 text-xs text-gray-700">
                                    {m.positionName || <span className="text-gray-300">—</span>}
                                  </td>
                                  <td className="px-3 py-2 text-xs text-gray-900">
                                    <div className="font-medium">{m.nameFr}</div>
                                    <div className="text-[10px] text-gray-400 font-mono mt-0.5 truncate max-w-[220px]">
                                      {m.code}
                                    </div>
                                  </td>
                                  {selectedFamily.source === 'manual' && (
                                    <td className="px-3 py-2">
                                      <button
                                        onClick={() => handleRemoveSkill(m.id)}
                                        className="w-6 h-6 rounded-full bg-gray-100 hover:bg-red-100 text-gray-500 hover:text-red-600 flex items-center justify-center text-xs transition-colors"
                                        title="Retirer"
                                      >
                                        ✕
                                      </button>
                                    </td>
                                  )}
                                </tr>
                              ))}
                              {filteredMembers.length === 0 && (
                                <tr>
                                  <td colSpan={selectedFamily.source === 'manual' ? 5 : 4} className="px-3 py-6 text-center text-xs text-gray-400">
                                    Aucun membre ne correspond au filtre
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {selectedFamily.source === 'manual' && (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-5 py-3 border-b border-gray-100">
                    <h3 className="text-sm font-semibold text-gray-900">Ajouter une compétence</h3>
                  </div>
                  <div className="p-4 space-y-3">
                    <input
                      type="text"
                      value={searchSkill}
                      onChange={(e) => setSearchSkill(e.target.value)}
                      placeholder="Rechercher une compétence..."
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                    />
                    {searchSkill.length >= 2 && (
                      <div className="max-h-48 overflow-y-auto border border-gray-100 rounded-lg divide-y divide-gray-50">
                        {filteredAvailableSkills.slice(0, 20).map((sk) => (
                          <button
                            key={sk.id}
                            onClick={() => handleAddSkill(sk.id)}
                            disabled={addingSkill === sk.id}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-green-50 transition-colors flex items-center justify-between disabled:opacity-40"
                          >
                            <span className="text-gray-900">{sk.nameFr}</span>
                            <span className="text-xs text-green-600 font-medium">+ Ajouter</span>
                          </button>
                        ))}
                        {filteredAvailableSkills.length === 0 && (
                          <div className="px-3 py-4 text-center text-xs text-gray-400">Aucun résultat</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {familyMembers.length >= 2 && (
                <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
                  <h4 className="text-xs font-semibold text-slate-800 mb-1">Effet matching</h4>
                  <p className="text-xs text-slate-600">
                    Un candidat avec cette compétence matche les offres qui demandent n&apos;importe
                    quel autre membre de la famille (score famille 0,7 — ou 0,9 si libellé exact).
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Nouvelle famille manuelle</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                <input
                  type="text"
                  value={newFamilyCode}
                  onChange={(e) => setNewFamilyCode(e.target.value)}
                  placeholder="ex: accueil_client"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                <input
                  type="text"
                  value={newFamilyName}
                  onChange={(e) => setNewFamilyName(e.target.value)}
                  placeholder="ex: Accueil client"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newFamilyDesc}
                  onChange={(e) => setNewFamilyDesc(e.target.value)}
                  placeholder="Compétences liées à l'accueil et la relation client..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20 resize-none"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-sm text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleCreateFamily}
                disabled={!newFamilyCode.trim() || !newFamilyName.trim() || creating}
                className="px-4 py-2 text-sm font-medium text-white bg-[#00244F] hover:bg-[#001B3D] rounded-lg transition-colors disabled:opacity-40"
              >
                {creating ? 'Création...' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SkillFamilies;
